/**
 * USD → CAD amounts for the marketing pricing page on the Canadian site.
 *
 * The pricing copy in `public.pricing` (en.json / fr.json) is written in US
 * dollars. On the .ca site every dollar amount is swapped for its figure on
 * the Canadian sheet, `CAD_PRICE_SHEET` in `src/config/billingCurrency.ts`,
 * which is what the Canada stack actually bills. This map must match that
 * sheet exactly; `src/__tests__/cadSubscriptionPricing.test.ts` pins them.
 *
 * Kept free of React and i18n imports so that API-side test can import it.
 */
export const CAD_MARKETING_AMOUNTS: Map<number, number> = new Map([
  // Plans (monthly)
  [149, 199],
  [399, 549],
  [699, 959],
  [1500, 2059],
  // Setup fees
  [495, 679],
  [995, 1359],
  [1495, 2049],
  // Add-ons (149 is shared with Starter above)
  [199, 269],
  [249, 339],
  [349, 479],
  [499, 679],
  [1000, 1369],
  // Additional integration, API overage block
  [75, 99],
  [25, 35],
]);

const groupDigits = (value: number, separator: string): string =>
  String(value).replace(/\B(?=(\d{3})+(?!\d))/g, separator);

/**
 * Rewrite the dollar amounts in one line of pricing copy. English writes
 * "$1,495"; French writes "1 495 $". Amounts the sheet does not name, and
 * numbers that are not prices ("5,000 calls"), are left as written.
 */
export function localizeCadAmounts(text: string, language: string): string {
  if (language.startsWith('fr')) {
    return text.replace(/(\d{1,3}(?: \d{3})+|\d+) \$/g, (whole, digits: string) => {
      const cad = CAD_MARKETING_AMOUNTS.get(Number(digits.replace(/ /g, '')));
      return cad === undefined ? whole : `${groupDigits(cad, ' ')} $`;
    });
  }
  return text.replace(/\$(\d{1,3}(?:,\d{3})+|\d+)(?!\d)/g, (whole, digits: string) => {
    const cad = CAD_MARKETING_AMOUNTS.get(Number(digits.replace(/,/g, '')));
    return cad === undefined ? whole : `$${groupDigits(cad, ',')}`;
  });
}

/** `localizeCadAmounts` over every string in a translated object or array. */
export function localizeCadCopy<T>(value: T, language: string): T {
  if (typeof value === 'string') return localizeCadAmounts(value, language) as unknown as T;
  if (Array.isArray(value)) return value.map((item) => localizeCadCopy(item, language)) as unknown as T;
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, item]) => [key, localizeCadCopy(item, language)]),
    ) as T;
  }
  return value;
}
